<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row>
        <b-col>
          <h1>Gestión de Costos</h1>
          <p class="lead">Configura políticas de costos y ejecuta recálculos para el sistema de inventario.</p>
        </b-col>
      </b-row>

      <!-- Navigation Tabs -->
      <b-tabs content-class="mt-3">
        <b-tab title="Configuración de Costos" active>
          <b-card>
            <template #header>
              <h5 class="mb-0">Configuración de Política de Costos</h5>
            </template>

            <div v-if="settingsLoading" class="text-center p-4">
              <b-spinner class="mr-2"></b-spinner>
              Cargando configuración...
            </div>

            <b-form v-else @submit.prevent="saveCostSettings">
              <b-row>
                <b-col md="12">
                  <b-form-group>
                    <b-form-checkbox
                      v-model="costSettings.use_origin_cost_on_transfer"
                      :value="true"
                      :unchecked-value="false">
                      <strong>Usar Costo de Origen en Transferencias</strong>
                    </b-form-checkbox>
                    <b-form-text class="text-muted">
                      Cuando está habilitado, las transferencias internas utilizan los costos reales de la ubicación de origen.
                      Cuando está deshabilitado, utilizan el último precio de compra.
                    </b-form-text>
                  </b-form-group>
                </b-col>

                <b-col md="12">
                  <b-form-group>
                    <b-form-checkbox
                      v-model="costSettings.allow_cost_recalculation"
                      :value="true"
                      :unchecked-value="false">
                      <strong>Permitir Recálculo de Costos</strong>
                    </b-form-checkbox>
                    <b-form-text class="text-muted">
                      Habilita la funcionalidad para recalcular costos de movimientos históricos.
                    </b-form-text>
                  </b-form-group>
                </b-col>

                <b-col md="12">
                  <b-form-group>
                    <b-form-checkbox
                      v-model="costSettings.auto_fill_zero_cost"
                      :value="true"
                      :unchecked-value="false">
                      <strong>Auto-llenar Costos en Cero</strong>
                    </b-form-checkbox>
                    <b-form-text class="text-muted">
                      Automáticamente llena costos de 0 o 1 en órdenes de compra con el último precio de compra disponible.
                    </b-form-text>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr>

              <div class="text-right">
                <b-button
                  type="submit"
                  variant="primary"
                  :disabled="settingsSaving">
                  <b-spinner small v-if="settingsSaving" class="mr-2"></b-spinner>
                  {{ settingsSaving ? 'Guardando...' : 'Guardar Configuración' }}
                </b-button>
              </div>
            </b-form>
          </b-card>
        </b-tab>

        <b-tab title="Recálculo de Costos">
          <b-card>
            <template #header>
              <h5 class="mb-0">Recálculo de Movimientos de Inventario</h5>
            </template>

            <b-alert show variant="warning">
              <b-icon icon="exclamation-triangle"></b-icon>
              <strong>Precaución:</strong> El recálculo de costos reprocessará todos los movimientos de inventario
              en el rango de fechas seleccionado. Esta operación puede tomar tiempo y afectará los reportes históricos.
            </b-alert>

            <b-form @submit.prevent="showRecalculationModal">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Fecha de Inicio:" label-for="start-date">
                    <b-form-datepicker
                      id="start-date"
                      v-model="recalculationForm.startDate"
                      :max="recalculationForm.endDate || new Date()"
                      placeholder="Selecciona fecha de inicio"
                      locale="es"
                      value-as-date
                      required>
                    </b-form-datepicker>
                    <small class="text-muted" v-if="recalculationForm.startDate">
                      Seleccionado: {{ recalculationForm.startDate }}
                    </small>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group label="Fecha de Fin:" label-for="end-date">
                    <b-form-datepicker
                      id="end-date"
                      v-model="recalculationForm.endDate"
                      :min="recalculationForm.startDate"
                      :max="new Date()"
                      placeholder="Selecciona fecha de fin"
                      locale="es"
                      value-as-date
                      required>
                    </b-form-datepicker>
                    <small class="text-muted" v-if="recalculationForm.endDate">
                      Seleccionado: {{ recalculationForm.endDate }}
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right">
                <b-button
                  type="submit"
                  variant="danger"
                  :disabled="!isValidDateRange || !costSettings.allow_cost_recalculation">
                  <b-icon icon="arrow-clockwise"></b-icon>
                  Iniciar Recálculo
                </b-button>
              </div>
            </b-form>

            <!-- Active Recalculations -->
            <div v-if="activeRecalculations.length > 0" class="mt-4">
              <hr>
              <h6>Recálculos en Progreso</h6>
              <b-table
                :items="activeRecalculations"
                :fields="recalculationFields"
                striped hover small>
                <template #cell(status)="row">
                  <b-badge :variant="getStatusVariant(row.item.status)">
                    {{ getStatusText(row.item.status) }}
                  </b-badge>
                </template>
                <template #cell(progress)="row">
                  <b-progress
                    v-if="row.item.status === 'processing'"
                    :value="row.item.progress || 0"
                    :max="100"
                    striped
                    animated>
                  </b-progress>
                  <span v-else>-</span>
                </template>
                <template #cell(actions)="row">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="refreshRecalculationStatus(row.item.id)">
                    <b-icon icon="arrow-clockwise"></b-icon>
                  </b-button>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-tab>

        <b-tab title="Estadísticas">
          <b-card>
            <template #header>
              <h5 class="mb-0">Estadísticas del Sistema de Costos</h5>
            </template>

            <b-row>
              <b-col md="3">
                <b-card bg-variant="primary" text-variant="white" class="text-center">
                  <h2>{{ stats.suppliesWithLastPrice || 0 }}</h2>
                  <p class="mb-0">Insumos con Último Precio</p>
                </b-card>
              </b-col>

              <b-col md="3">
                <b-card bg-variant="success" text-variant="white" class="text-center">
                  <h2>{{ stats.totalRecalculations || 0 }}</h2>
                  <p class="mb-0">Recálculos Realizados</p>
                </b-card>
              </b-col>

              <b-col md="3">
                <b-card bg-variant="info" text-variant="white" class="text-center">
                  <h2>{{ stats.temporalPrices || 0 }}</h2>
                  <p class="mb-0">Precios Temporales</p>
                </b-card>
              </b-col>

              <b-col md="3">
                <b-card bg-variant="warning" text-variant="white" class="text-center">
                  <h2>{{ costSettings.auto_fill_zero_cost ? 'ON' : 'OFF' }}</h2>
                  <p class="mb-0">Auto-llenado</p>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <b-tab title="Cola de Costeo" @click="loadCostQueue">
          <b-card>
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Cola de Procesamiento Asíncrono de Costos</h5>
                <div>
                  <b-button size="sm" variant="outline-secondary" @click="loadCostQueue" :disabled="queueLoading">
                    <b-spinner small v-if="queueLoading" class="mr-1"></b-spinner>
                    Refrescar
                  </b-button>
                  <b-button size="sm" variant="warning" class="ml-2"
                           :disabled="queueRetrying || !(costQueue.stats && costQueue.stats.failed)"
                           @click="retryFailedJobs">
                    <b-spinner small v-if="queueRetrying" class="mr-1"></b-spinner>
                    Reintentar fallidos
                  </b-button>
                </div>
              </div>
            </template>

            <b-alert show variant="info">
              Los movimientos de inventario encolan el cálculo de costo y un worker en segundo plano
              consume la cola cada 30 segundos. Aquí puedes ver el estado y reintentar los que fallen.
            </b-alert>

            <b-row class="mb-3">
              <b-col md="3">
                <b-card bg-variant="warning" text-variant="white" class="text-center">
                  <h2>{{ (costQueue.stats && costQueue.stats.pending) || 0 }}</h2>
                  <p class="mb-0">Pendientes</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card bg-variant="info" text-variant="white" class="text-center">
                  <h2>{{ (costQueue.stats && costQueue.stats.processing) || 0 }}</h2>
                  <p class="mb-0">Procesando</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card bg-variant="danger" text-variant="white" class="text-center">
                  <h2>{{ (costQueue.stats && costQueue.stats.failed) || 0 }}</h2>
                  <p class="mb-0">Fallidos</p>
                </b-card>
              </b-col>
              <b-col md="3">
                <b-card bg-variant="success" text-variant="white" class="text-center">
                  <h2>{{ (costQueue.stats && costQueue.stats.completed) || 0 }}</h2>
                  <p class="mb-0">Completados</p>
                </b-card>
              </b-col>
            </b-row>

            <h6>Últimos jobs fallidos</h6>
            <b-table
              v-if="costQueue.failed && costQueue.failed.length"
              :items="costQueue.failed"
              :fields="queueFailedFields"
              small
              striped>
              <template #cell(last_error)="row">
                <span class="text-danger" :title="row.value">{{ truncateError(row.value) }}</span>
              </template>
            </b-table>
            <p v-else class="text-muted">No hay jobs fallidos.</p>
          </b-card>
        </b-tab>
      </b-tabs>

      <!-- Cost Recalculation Modal -->
      <CostRecalculationModal
        v-model="showModal"
        :date-range="recalculationForm"
        @recalculation-started="onRecalculationStarted"
        @recalculation-completed="onRecalculationCompleted" />

    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import CostRecalculationModal from "../components/CostRecalculationModal.vue";
import axios from "axios";

export default {
  name: "CostManagement",
  components: {
    Nav,
    CostRecalculationModal
  },
  data() {
    return {
      costSettings: {
        use_origin_cost_on_transfer: true,
        allow_cost_recalculation: true,
        auto_fill_zero_cost: true
      },
      settingsLoading: false,
      settingsSaving: false,
      recalculationForm: {
        startDate: null,
        endDate: null
      },
      activeRecalculations: [],
      showModal: false,
      stats: {},
      costQueue: { stats: null, failed: [] },
      queueLoading: false,
      queueRetrying: false,
      queueFailedFields: [
        { key: 'id', label: 'Job' },
        { key: 'stock_move_id', label: 'Stock Move' },
        { key: 'attempts', label: 'Intentos' },
        { key: 'last_error', label: 'Error' },
        { key: 'finished_at', label: 'Falló en' }
      ],
      recalculationFields: [
        { key: 'id', label: 'ID' },
        { key: 'start_date', label: 'Fecha Inicio' },
        { key: 'end_date', label: 'Fecha Fin' },
        { key: 'status', label: 'Estado' },
        { key: 'progress', label: 'Progreso' },
        { key: 'affected_movements_count', label: 'Movimientos' },
        { key: 'actions', label: 'Acciones' }
      ]
    };
  },
  computed: {
    isValidDateRange() {
      const hasStartDate = !!this.recalculationForm.startDate;
      const hasEndDate = !!this.recalculationForm.endDate;
      const isValidOrder = hasStartDate && hasEndDate &&
                          new Date(this.recalculationForm.startDate) <= new Date(this.recalculationForm.endDate);

      console.log('Date validation:', {
        startDate: this.recalculationForm.startDate,
        endDate: this.recalculationForm.endDate,
        hasStartDate,
        hasEndDate,
        isValidOrder
      });

      return isValidOrder;
    }
  },
  mounted() {
    this.loadCostSettings();
    this.loadActiveRecalculations();
    this.loadStats();
  },
  methods: {
    async loadCostSettings() {
      this.settingsLoading = true;
      try {
        const response = await axios.get('/cost/settings');
        this.costSettings = response.data;
      } catch (error) {
        this.makeToast('Error', 'No se pudieron cargar las configuraciones', 'danger');
        console.error('Error loading cost settings:', error);
      } finally {
        this.settingsLoading = false;
      }
    },

    async saveCostSettings() {
      this.settingsSaving = true;
      try {
        await axios.put('/cost/settings', this.costSettings);
        this.makeToast('Éxito', 'Configuración guardada correctamente', 'success');
      } catch (error) {
        this.makeToast('Error', 'Error al guardar la configuración', 'danger');
        console.error('Error saving cost settings:', error);
      } finally {
        this.settingsSaving = false;
      }
    },

    async loadActiveRecalculations() {
      try {
        const response = await axios.get('/cost/recalculation-status');
        this.activeRecalculations = response.data.filter(r =>
          ['pending', 'processing'].includes(r.status)
        );
      } catch (error) {
        console.error('Error loading recalculations:', error);
      }
    },

    async loadStats() {
      try {
        const response = await axios.get('/cost/statistics');
        this.stats = response.data;
      } catch (error) {
        console.error('Error loading statistics:', error);
      }
    },

    showRecalculationModal() {
      if (!this.isValidDateRange) {
        this.makeToast('Error', 'Selecciona un rango de fechas válido', 'warning');
        return;
      }

      // Additional validation
      if (!this.recalculationForm.startDate || !this.recalculationForm.endDate) {
        this.makeToast('Error', 'Ambas fechas son requeridas', 'warning');
        return;
      }

      this.showModal = true;
    },

    onRecalculationStarted(recalculationId) {
      this.makeToast('Éxito', `Recálculo iniciado (ID: ${recalculationId})`, 'success');
      this.loadActiveRecalculations();
    },

    onRecalculationCompleted() {
      this.loadActiveRecalculations();
      this.loadStats();
    },

    async refreshRecalculationStatus(recalculationId) {
      try {
        const response = await axios.get(`/cost/recalculation-status/${recalculationId}`);
        const index = this.activeRecalculations.findIndex(r => r.id === recalculationId);
        if (index !== -1) {
          this.activeRecalculations.splice(index, 1, response.data);
        }
      } catch (error) {
        console.error('Error refreshing status:', error);
      }
    },

    getStatusVariant(status) {
      const variants = {
        'pending': 'warning',
        'processing': 'info',
        'completed': 'success',
        'failed': 'danger'
      };
      return variants[status] || 'secondary';
    },

    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'processing': 'Procesando',
        'completed': 'Completado',
        'failed': 'Fallido'
      };
      return texts[status] || status;
    },

    makeToast(title, message, variant = 'info') {
      this.$bvToast.toast(message, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: 5000
      });
    },

    async loadCostQueue() {
      this.queueLoading = true;
      try {
        const response = await axios.get('/cost/queue');
        this.costQueue = {
          stats: response.data.stats || {},
          failed: response.data.failed || []
        };
      } catch (error) {
        this.makeToast('Error', 'No se pudo cargar la cola de costeo', 'danger');
        console.error('Error loading cost queue:', error);
      } finally {
        this.queueLoading = false;
      }
    },

    async retryFailedJobs() {
      this.queueRetrying = true;
      try {
        const response = await axios.post('/cost/queue/retry-failed');
        const requeued = response.data && response.data.requeued ? response.data.requeued : 0;
        this.makeToast('Reintento', `${requeued} job(s) re-encolados`, 'success');
        await this.loadCostQueue();
      } catch (error) {
        this.makeToast('Error', 'No se pudieron reintentar los jobs', 'danger');
        console.error('Error retrying jobs:', error);
      } finally {
        this.queueRetrying = false;
      }
    },

    truncateError(msg) {
      if (!msg) return '';
      return msg.length > 80 ? msg.slice(0, 77) + '...' : msg;
    }
  }
};
</script>

<style scoped>
.documentation h6 {
  color: #495057;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.documentation p, .documentation li {
  color: #6c757d;
}
</style>